import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Trophy, X, Calendar, MapPin, Award, ArrowRight, User, ChevronLeft, ChevronRight } from 'lucide-react';
import { useTheme } from '../../context/ThemeContext';

const ResultsGallery = () => {
  const { isDarkMode } = useTheme();
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedResult, setSelectedResult] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);

  // Fetch results from API
  useEffect(() => {
    const fetchResults = async () => {
      try {
        const response = await fetch('https://api.fsgu.co.za/get-results.php?status=published&limit=8');
        const data = await response.json();
        if (data.success && data.data) {
          setResults(data.data);
        }
      } catch (error) {
        console.error('Error fetching results:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchResults();
  }, []);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (selectedResult) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [selectedResult]);

  // Handle keyboard navigation in modal
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!selectedResult) return;
      if (e.key === 'Escape') closeModal();
      if (e.key === 'ArrowLeft') navigatePrev();
      if (e.key === 'ArrowRight') navigateNext();
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedResult, currentIndex]);

  const openModal = (result, index) => {
    setSelectedResult(result);
    setCurrentIndex(index);
  };

  const closeModal = () => {
    setSelectedResult(null);
  };

  const navigatePrev = () => {
    const newIndex = currentIndex === 0 ? results.length - 1 : currentIndex - 1;
    setCurrentIndex(newIndex);
    setSelectedResult(results[newIndex]);
  };

  const navigateNext = () => {
    const newIndex = currentIndex === results.length - 1 ? 0 : currentIndex + 1;
    setCurrentIndex(newIndex);
    setSelectedResult(results[newIndex]);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('en-ZA', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  const formatCategory = (category) => {
    if (!category) return '';
    return category.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  // Placeholder image for results without winner photos
  const getResultImage = (result) => {
    if (result.winner_photo) return result.winner_photo;
    if (result.featured_image_url) return result.featured_image_url;
    // Return a golf-themed placeholder based on category
    const placeholders = {
      'Mens_Open': 'https://images.unsplash.com/photo-1535131749006-b7f58c99034b?w=800&h=600&fit=crop',
      'Ladies_Open': 'https://images.unsplash.com/photo-1612872087720-bb876e2e67d1?w=800&h=600&fit=crop',
      'Junior_Boys': 'https://images.unsplash.com/photo-1530915365347-e35b749a0381?w=800&h=600&fit=crop',
      'Junior_Girls': 'https://images.unsplash.com/photo-1592919505780-303950717480?w=800&h=600&fit=crop',
      'default': 'https://images.unsplash.com/photo-1587174486073-ae5e5cff23aa?w=800&h=600&fit=crop'
    };
    return placeholders[result.category] || placeholders['default'];
  };

  if (loading) {
    return (
      <section className={`section ${isDarkMode ? 'bg-fsgu-gray-800/50' : 'bg-fsgu-silver-50'}`}>
        <div className="container-custom">
          <div className="flex items-center justify-center py-16">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-fsgu-green-500"></div>
          </div>
        </div>
      </section>
    );
  }

  if (results.length === 0) {
    return null; // Don't render section if no results
  }

  return (
    <section className={`section ${isDarkMode ? 'bg-fsgu-gray-800/50' : 'bg-fsgu-silver-50'}`}>
      <div className="container-custom">
        {/* Section Header */}
        <div className="text-center mb-12">
          <div className={`inline-flex items-center justify-center w-16 h-16 rounded-2xl mb-6 ${
            isDarkMode ? 'bg-fsgu-gold-500/20' : 'bg-fsgu-gold-500/10'
          }`}>
            <Trophy className={`w-8 h-8 ${
              isDarkMode ? 'text-fsgu-gold-400' : 'text-fsgu-gold-500'
            }`} />
          </div>

          <h2 className={`text-3xl sm:text-4xl font-bold mb-4 font-cursive ${
            isDarkMode ? 'text-fsgu-silver-100' : 'text-fsgu-silver-900'
          }`}>
            Recent Champions
          </h2>

          <p className={`text-lg mb-2 ${
            isDarkMode ? 'text-fsgu-silver-400' : 'text-fsgu-silver-600'
          }`}>
            Celebrating our latest tournament winners
          </p>
        </div>

        {/* Gallery Grid */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
          {results.slice(0, 8).map((result, index) => (
            <button
              key={result.id}
              onClick={() => openModal(result, index)}
              className={`group relative aspect-[3/4] rounded-xl overflow-hidden shadow-lg transition-all duration-300 hover:scale-[1.02] hover:shadow-2xl focus:outline-none focus:ring-2 focus:ring-fsgu-green-500 ${
                isDarkMode ? 'bg-fsgu-gray-800' : 'bg-white'
              }`}
            >
              {/* Image */}
              <img
                src={getResultImage(result)}
                alt={result.winner_name || result.tournament_name}
                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                loading="lazy"
              />

              {/* Gradient Overlay */}
              <div className={`absolute inset-0 bg-gradient-to-t ${
                isDarkMode 
                  ? 'from-fsgu-gray-900 via-fsgu-gray-900/40 to-transparent' 
                  : 'from-black/80 via-black/30 to-transparent'
              }`} />

              {/* Trophy Badge */}
              <div className={`absolute top-3 right-3 p-2 rounded-full backdrop-blur-md ${
                isDarkMode
                  ? 'bg-fsgu-gold-500/90 text-fsgu-gray-900'
                  : 'bg-fsgu-gold-500 text-white'
              } shadow-lg`}>
                <Trophy className="w-4 h-4" />
              </div>

              {/* Content */}
              <div className="absolute bottom-0 left-0 right-0 p-4">
                {/* Winner Name */}
                <h3 className="text-white font-bold text-lg leading-tight mb-1 drop-shadow-lg">
                  {result.winner_name || 'Champion'}
                </h3>

                {/* Tournament Name */}
                <p className={`text-sm font-medium mb-1 ${
                  isDarkMode ? 'text-fsgu-gold-300' : 'text-fsgu-gold-400'
                }`}>
                  {result.tournament_name}
                </p>

                {/* Score */}
                {result.winner_score && (
                  <p className="text-white/80 text-sm">
                    {result.winner_score}
                  </p>
                )}

                {/* Hover indicator */}
                <div className="mt-2 flex items-center text-white/60 text-xs opacity-0 group-hover:opacity-100 transition-opacity">
                  <span>View Details</span>
                  <ArrowRight className="w-3 h-3 ml-1" />
                </div>
              </div>
            </button>
          ))}
        </div>

        {/* View All Results Button */}
        <div className="text-center mt-10">
          <Link 
            to="/results" 
            className="btn btn-primary inline-flex items-center"
          >
            <Trophy className="w-5 h-5 mr-2" />
            View All Results
            <ArrowRight className="w-5 h-5 ml-2" />
          </Link>
        </div>
      </div>

      {/* Modal */}
      {selectedResult && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
          onClick={closeModal}
        >
          {/* Navigation Arrows - Desktop */}
          <button
            onClick={(e) => { e.stopPropagation(); navigatePrev(); }}
            className={`hidden md:flex absolute left-4 lg:left-8 p-3 rounded-full transition-all ${
              isDarkMode
                ? 'bg-fsgu-gray-800/80 hover:bg-fsgu-gray-700 text-white'
                : 'bg-white/80 hover:bg-white text-fsgu-gray-900'
            } backdrop-blur-sm shadow-lg`}
          >
            <ChevronLeft className="w-6 h-6" />
          </button>

          <button
            onClick={(e) => { e.stopPropagation(); navigateNext(); }}
            className={`hidden md:flex absolute right-4 lg:right-8 p-3 rounded-full transition-all ${
              isDarkMode
                ? 'bg-fsgu-gray-800/80 hover:bg-fsgu-gray-700 text-white'
                : 'bg-white/80 hover:bg-white text-fsgu-gray-900'
            } backdrop-blur-sm shadow-lg`}
          >
            <ChevronRight className="w-6 h-6" />
          </button>

          {/* Modal Content */}
          <div 
            className={`relative w-full max-w-4xl max-h-[90vh] rounded-2xl overflow-hidden shadow-2xl ${
              isDarkMode ? 'bg-fsgu-gray-800' : 'bg-white'
            }`}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close Button */}
            <button
              onClick={closeModal}
              className={`absolute top-4 right-4 z-10 p-2 rounded-full transition-colors ${
                isDarkMode
                  ? 'bg-fsgu-gray-900/80 hover:bg-fsgu-gray-700 text-white'
                  : 'bg-white/80 hover:bg-white text-fsgu-gray-900'
              } backdrop-blur-sm shadow-lg`}
            >
              <X className="w-5 h-5" />
            </button>

            {/* Modal Layout */}
            <div className="flex flex-col md:flex-row max-h-[90vh] overflow-y-auto md:overflow-hidden">
              {/* Image Side */}
              <div className="md:w-1/2 relative">
                <img
                  src={getResultImage(selectedResult)}
                  alt={selectedResult.winner_name || selectedResult.tournament_name}
                  className="w-full h-64 md:h-full object-cover"
                />
                {/* Image overlay gradient */}
                <div className={`absolute inset-0 bg-gradient-to-t md:bg-gradient-to-r ${
                  isDarkMode 
                    ? 'from-fsgu-gray-800 via-transparent to-transparent' 
                    : 'from-white via-transparent to-transparent'
                } md:opacity-50`} />

                {/* Mobile Navigation */}
                <div className="md:hidden absolute bottom-4 left-0 right-0 flex justify-center gap-4">
                  <button
                    onClick={navigatePrev}
                    className={`p-2 rounded-full ${
                      isDarkMode
                        ? 'bg-fsgu-gray-900/80 text-white'
                        : 'bg-white/80 text-fsgu-gray-900'
                    } backdrop-blur-sm shadow-lg`}
                  >
                    <ChevronLeft className="w-5 h-5" />
                  </button>
                  <button
                    onClick={navigateNext}
                    className={`p-2 rounded-full ${
                      isDarkMode
                        ? 'bg-fsgu-gray-900/80 text-white'
                        : 'bg-white/80 text-fsgu-gray-900'
                    } backdrop-blur-sm shadow-lg`}
                  >
                    <ChevronRight className="w-5 h-5" />
                  </button>
                </div>
              </div>

              {/* Content Side */}
              <div className="md:w-1/2 p-6 md:p-8 md:overflow-y-auto">
                {/* Category Badge */}
                {selectedResult.category && (
                  <span className={`inline-block px-3 py-1 rounded-full text-xs font-medium mb-4 ${
                    isDarkMode
                      ? 'bg-fsgu-green-900/30 text-fsgu-green-400'
                      : 'bg-fsgu-green-100 text-fsgu-green-700'
                  }`}>
                    {formatCategory(selectedResult.category)}
                  </span>
                )}

                {/* Tournament Name */}
                <h2 className={`text-2xl md:text-3xl font-bold mb-2 ${
                  isDarkMode ? 'text-fsgu-silver-100' : 'text-fsgu-silver-900'
                }`}>
                  {selectedResult.tournament_name}
                </h2>

                {/* Winner Info */}
                <div className={`flex items-center gap-3 mb-6 p-4 rounded-xl ${
                  isDarkMode ? 'bg-fsgu-gold-900/20' : 'bg-fsgu-gold-50'
                }`}>
                  <div className={`p-3 rounded-full ${
                    isDarkMode ? 'bg-fsgu-gold-500/30' : 'bg-fsgu-gold-100'
                  }`}>
                    <Trophy className={`w-6 h-6 ${
                      isDarkMode ? 'text-fsgu-gold-400' : 'text-fsgu-gold-600'
                    }`} />
                  </div>
                  <div>
                    <p className={`text-sm ${
                      isDarkMode ? 'text-fsgu-silver-400' : 'text-fsgu-silver-600'
                    }`}>
                      Champion
                    </p>
                    <p className={`text-xl font-bold ${
                      isDarkMode ? 'text-fsgu-gold-400' : 'text-fsgu-gold-600'
                    }`}>
                      {selectedResult.winner_name || 'TBA'}
                    </p>
                    {selectedResult.winner_score && (
                      <p className={`text-sm font-medium ${
                        isDarkMode ? 'text-fsgu-silver-300' : 'text-fsgu-silver-700'
                      }`}>
                        Score: {selectedResult.winner_score}
                      </p>
                    )}
                  </div>
                </div>

                {/* Details Grid */}
                <div className="space-y-4 mb-6">
                  {selectedResult.tournament_date && (
                    <div className="flex items-center gap-3">
                      <Calendar className={`w-5 h-5 ${
                        isDarkMode ? 'text-fsgu-green-400' : 'text-fsgu-green-500'
                      }`} />
                      <div>
                        <p className={`text-xs ${
                          isDarkMode ? 'text-fsgu-silver-500' : 'text-fsgu-silver-500'
                        }`}>Date</p>
                        <p className={`font-medium ${
                          isDarkMode ? 'text-fsgu-silver-200' : 'text-fsgu-silver-800'
                        }`}>
                          {formatDate(selectedResult.tournament_date)}
                        </p>
                      </div>
                    </div>
                  )}

                  {selectedResult.venue && (
                    <div className="flex items-center gap-3">
                      <MapPin className={`w-5 h-5 ${
                        isDarkMode ? 'text-fsgu-green-400' : 'text-fsgu-green-500'
                      }`} />
                      <div>
                        <p className={`text-xs ${
                          isDarkMode ? 'text-fsgu-silver-500' : 'text-fsgu-silver-500'
                        }`}>Venue</p>
                        <p className={`font-medium ${
                          isDarkMode ? 'text-fsgu-silver-200' : 'text-fsgu-silver-800'
                        }`}>
                          {selectedResult.venue}
                        </p>
                      </div>
                    </div>
                  )}

                  {selectedResult.winner_club && (
                    <div className="flex items-center gap-3">
                      <User className={`w-5 h-5 ${
                        isDarkMode ? 'text-fsgu-green-400' : 'text-fsgu-green-500'
                      }`} />
                      <div>
                        <p className={`text-xs ${
                          isDarkMode ? 'text-fsgu-silver-500' : 'text-fsgu-silver-500'
                        }`}>Club</p>
                        <p className={`font-medium ${
                          isDarkMode ? 'text-fsgu-silver-200' : 'text-fsgu-silver-800'
                        }`}>
                          {selectedResult.winner_club}
                        </p>
                      </div>
                    </div>
                  )}

                  {selectedResult.format && (
                    <div className="flex items-center gap-3">
                      <Award className={`w-5 h-5 ${
                        isDarkMode ? 'text-fsgu-green-400' : 'text-fsgu-green-500'
                      }`} />
                      <div>
                        <p className={`text-xs ${
                          isDarkMode ? 'text-fsgu-silver-500' : 'text-fsgu-silver-500'
                        }`}>Format</p>
                        <p className={`font-medium ${
                          isDarkMode ? 'text-fsgu-silver-200' : 'text-fsgu-silver-800'
                        }`}>
                          {selectedResult.format}
                        </p>
                      </div>
                    </div>
                  )}
                </div>

                {/* Report Preview */}
                {selectedResult.report_content && (
                  <div className={`mb-6 p-4 rounded-xl ${
                    isDarkMode ? 'bg-fsgu-gray-900/50' : 'bg-fsgu-silver-50'
                  }`}>
                    <p className={`text-sm line-clamp-4 ${
                      isDarkMode ? 'text-fsgu-silver-400' : 'text-fsgu-silver-600'
                    }`}>
                      {selectedResult.report_content.replace(/<[^>]*>/g, '').substring(0, 300)}...
                    </p>
                  </div>
                )}

                {/* Action Button */}
                <Link
                  to={`/results/${selectedResult.slug || selectedResult.id}`}
                  onClick={closeModal}
                  className="btn btn-primary w-full inline-flex items-center justify-center"
                >
                  Read Full Report
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Link>

                {/* Pagination Indicator */}
                <div className="flex justify-center gap-2 mt-6">
                  {results.slice(0, 8).map((_, idx) => (
                    <button
                      key={idx}
                      onClick={() => {
                        setCurrentIndex(idx);
                        setSelectedResult(results[idx]);
                      }}
                      className={`w-2 h-2 rounded-full transition-all ${
                        idx === currentIndex
                          ? 'w-6 bg-fsgu-green-500'
                          : isDarkMode
                          ? 'bg-fsgu-gray-600 hover:bg-fsgu-gray-500'
                          : 'bg-fsgu-silver-300 hover:bg-fsgu-silver-400'
                      }`}
                    />
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default ResultsGallery;